import {
  ACCEPT_IMAGES,
  ACCEPT_PDF,
  ACCEPT_VIDEO,
  MAX_FILE_SIZE_PUBNUB,
  MAX_PDF_FILE_SIZE,
  ONE_MB,
  messageStrings,
  sendErrorMessages,
} from '../../constants';

// PDF・MIME は一斉送信と同じ値を使う。
// 片方だけ変えて UI 表示と実検証がずれるのを防ぐため、これらは再エクスポートに留める。
// 動画だけは配信経路が異なるため独立した上限を持つ（下の MAX_CHAT_VIDEO_SIZE 参照）
export const CHAT_PDF_MIME_TYPE = ACCEPT_PDF[0];
export const MAX_CHAT_PDF_SIZE = MAX_PDF_FILE_SIZE;
export const CHAT_VIDEO_MIME_PREFIX = 'video/';
// 個別チャットの動画はPubNub経由でmale画面に表示されるブラウザ再生で、LINEのvideo messageではない。
// LINE配信用の MAX_LINE_VIDEO_SIZE とは根拠が異なるため連動させず、
// male→管理（maleの MAX_CHAT_VIDEO_SIZE）と対称の100MBに保つ
export const MAX_CHAT_VIDEO_SIZE = ONE_MB * 100;

// ZIP は管理画面 → LINEユーザー（male）方向のみ送れる。正規 MIME は application/zip
export const CHAT_ZIP_MIME_TYPE = 'application/zip';
// ブラウザ/OS が .zip に付ける MIME の揺れ。選択直後に CHAT_ZIP_MIME_TYPE へ正規化する
export const CHAT_ZIP_MIME_ALIASES = [
  'application/x-zip-compressed',
  'application/x-zip',
  'multipart/x-zip',
];
// これらの汎用 MIME のときだけ拡張子で zip かどうかを判定する
const GENERIC_MIME_TYPES = ['', 'application/octet-stream'];
const ZIP_EXTENSION = '.zip';
// backend の ZIP_UPLOAD_MAX_SIZE_BYTES(300 * FRONTEND_MB_TO_BYTES) と必ず同一バイト数にすること
export const MAX_CHAT_ZIP_SIZE = ONE_MB * 300;

// 選択後検証用の許可リスト（正規 MIME のみ。完全一致で照合する）
export const CHAT_ACCEPTED_FILE_TYPES = [
  ...ACCEPT_IMAGES,
  ...ACCEPT_VIDEO,
  CHAT_PDF_MIME_TYPE,
  CHAT_ZIP_MIME_TYPE,
];

// input の accept 属性用。Windows のダイアログで .zip を選べるよう別名と拡張子も載せる
// （検証リストには混ぜない。別名は normalizeChatFile で正規 MIME に寄せてから検証する）
export const CHAT_INPUT_ACCEPT = [
  ...CHAT_ACCEPTED_FILE_TYPES,
  ...CHAT_ZIP_MIME_ALIASES,
  ZIP_EXTENSION,
].join(',');

// accept 属性はドラッグ&ドロップやダイアログの「すべてのファイル」で回避できるため、
// 選択後にも許可リストで検証する（未対応 MIME は image 扱いで送られてしまう）
export const isAcceptedChatFileType = (type?: string): boolean =>
  !!type && CHAT_ACCEPTED_FILE_TYPES.includes(type);

export type ChatFileKind = 'pdf' | 'video' | 'zip' | 'image';

// 添付ファイルの MIME からチャット上の種別を判定する。
// pdf / zip / video 以外（未知 MIME・未指定を含む）は image にフォールバックする
export const getChatFileKind = (type?: string): ChatFileKind =>
  type === CHAT_PDF_MIME_TYPE
    ? 'pdf'
    : type === CHAT_ZIP_MIME_TYPE
    ? 'zip'
    : type?.startsWith(CHAT_VIDEO_MIME_PREFIX)
    ? 'video'
    : 'image';

// image 以外は PubNub sendFile の 5MB 上限を超えるため S3 経由で送る
export type S3ChatFileKind = Exclude<ChatFileKind, 'image'>;
export const isS3ChatFileKind = (
  kind?: ChatFileKind,
): kind is S3ChatFileKind => kind !== undefined && kind !== 'image';

// ファイル名チップ／ダウンロードカードで表示する文書系種別。
// NOTE: male の utils/chatDocument.ts（getChatDocumentPresentation）と対になる。種別を増やすときは両方を更新すること
export type ChatDocumentKind = Extract<ChatFileKind, 'pdf' | 'zip'>;
export const isChatDocumentKind = (
  kind?: ChatFileKind,
): kind is ChatDocumentKind => kind === 'pdf' || kind === 'zip';
export const CHAT_DOCUMENT_STRINGS: Record<
  ChatDocumentKind,
  { fileName: string; unavailable: string }
> = {
  pdf: {
    fileName: messageStrings.pdfFileName,
    unavailable: messageStrings.pdfUnavailable,
  },
  zip: {
    fileName: messageStrings.zipFileName,
    unavailable: messageStrings.zipUnavailable,
  },
};

// 種別ごとのサイズ上限（Record にして種別追加時のチェック漏れをコンパイルエラーにする）
export const CHAT_FILE_SIZE_LIMIT: Record<
  ChatFileKind,
  { maxBytes: number; message: string }
> = {
  pdf: { maxBytes: MAX_CHAT_PDF_SIZE, message: messageStrings.pdfLimitSize },
  video: {
    maxBytes: MAX_CHAT_VIDEO_SIZE,
    message: messageStrings.videoLimitSize,
  },
  zip: { maxBytes: MAX_CHAT_ZIP_SIZE, message: messageStrings.zipLimitSize },
  image: {
    maxBytes: MAX_FILE_SIZE_PUBNUB,
    message: sendErrorMessages.limitSize,
  },
};

// 種別ごとの「送信しました」プレビュー／通知ラベル
export const CHAT_FILE_SENT_LABEL: Record<ChatFileKind, string> = {
  pdf: messageStrings.pdfSent,
  video: messageStrings.videoSent,
  zip: messageStrings.zipSent,
  image: messageStrings.image,
};

/**
 * 選択直後に呼ぶ。zip の別名／汎用 MIME + .zip を正規 MIME の File に再ラップする
 * （utils/richMenuImage.ts の normalizeRichMenuImageFileContentType と同方式。
 * new File([file]) はデータをコピーしない）。それ以外は同一インスタンスを返す
 */
export const normalizeChatFile = (file: File): File => {
  const type = file.type.toLowerCase();
  const isZip =
    type === CHAT_ZIP_MIME_TYPE ||
    CHAT_ZIP_MIME_ALIASES.includes(type) ||
    (GENERIC_MIME_TYPES.includes(type) &&
      file.name.toLowerCase().endsWith(ZIP_EXTENSION));
  if (!isZip || file.type === CHAT_ZIP_MIME_TYPE) return file;
  return new File([file], file.name, {
    type: CHAT_ZIP_MIME_TYPE,
    lastModified: file.lastModified,
  });
};
